import { useMemo } from 'react'
import zionDebug from '@fs/zion-debug'
import { useFeatureFlag } from '@fs/zion-flags'
import {
  doesImageHaveData,
  getDanglingRecords,
  getDanglingRecordsFromNextImages,
  getDanglingRecordsFromPreviousImages,
  sortPersonNameSubElements,
} from '@fs/zion-sls'

export default function useRecords({ images, currentImageArk, thumbnails }) {
  const currentImage = images?.find((i) => i.partitionKey === currentImageArk)
  const jiapuFlag = useFeatureFlag('rip_sharedImageViewer_showJiapu')
  const debug = zionDebug('rip:R2:SelectPerson') // localStorage.debug = "fs:rip:R2:SelectPerson"
  // Need to wait till updateDanglers has been called for all images before getting danglingRecords
  // The useMemo was agreesively caching the records before the persons were updated causing bug RIP-1463
  const imagesUpdated = images?.length > 0 && images?.every((img) => img.updated)

  const danglingRecordsFromPreviousImages = useMemo(() => {
    if (imagesUpdated && images?.length > 1 && currentImageArk && thumbnails) {
      const danglingRecordsFromPrevious = getDanglingRecordsFromPreviousImages(images, currentImageArk, thumbnails)
      if (danglingRecordsFromPrevious) {
        danglingRecordsFromPrevious.forEach((record) => {
          if (record?.subElements)
            record.subElements.forEach((person) => {
              sortPersonNameSubElements(person)
            })
        })
      }
      return danglingRecordsFromPrevious
    }
    return []
  }, [images, currentImageArk, thumbnails, imagesUpdated])

  const danglingRecordsFromNextImages = useMemo(() => {
    if (imagesUpdated && images?.length > 1 && currentImageArk && thumbnails) {
      return getDanglingRecordsFromNextImages(images, currentImageArk, thumbnails)
    }
    return []
  }, [imagesUpdated, images, currentImageArk, thumbnails])

  // Filtering subElements of type RECORD from current image, there might be floating persons as well but don't include them here
  const records = currentImage?.subElements?.filter((el) => el.elementType === 'RECORD') || []

  // Return any dangling records not found in the next or previous images. This can happen when a record is in another image group
  const currentDanglingRecords = useMemo(() => {
    // Get the base dangling records and filter out those already included from previous or next images
    const danglingRecords = getDanglingRecords(currentImage).filter(
      (rec) =>
        !danglingRecordsFromPreviousImages.some((pRec) => pRec.id === rec.id) &&
        !danglingRecordsFromNextImages.some((nRec) => nRec.id === rec.id)
    )

    // Transform into the expected structure
    return danglingRecords.map((record) => ({
      id: record.id,
      subElements: record.persons, // Put persons into subElements array
      elementType: 'RECORD', // Match the structure of regular records
    }))
  }, [currentImage, danglingRecordsFromPreviousImages, danglingRecordsFromNextImages])

  const floatingPersons =
    currentImage?.floatingElements?.filter(
      (el) =>
        el?.elementType === 'PERSON' &&
        (!el.superElements || el.superElements?.every((se) => se.id.startsWith('2:4:2'))) // show people in floating persons category if superElement is an sls tree
    ) || []

  const allRecords = [
    ...currentDanglingRecords,
    ...danglingRecordsFromPreviousImages,
    ...records.filter((record) => record?.subElements),
    ...danglingRecordsFromNextImages,
  ]
  const noPeople = allRecords?.length === 0 && floatingPersons?.length === 0

  const imageHasData = useMemo(() => (currentImage ? doesImageHaveData(currentImage) : null), [currentImage])
  const isJiapu =
    !jiapuFlag.isOn &&
    Boolean(
      currentImage?.subElements?.find((ele) => ele?.id.startsWith('2:4:')) ||
      currentImage?.floatingElements?.find((ele) => ele?.id.startsWith('2:4:'))
    )

  debug({
    floatingElements: currentImage?.floatingElements,
    jiapuFlag,
    currentImage,
    floatingPersons,
    allRecords,
    noPeople,
    imageHasData,
    isJiapu,
    currentDanglingRecords,
  })
  return { floatingPersons, allRecords, noPeople, imageHasData, isJiapu }
}
